import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ProviderDocumentStatus,
  ProviderDocumentType,
  deleteProviderDocument,
  listProviderDocuments,
  uploadProviderDocument,
  type ProviderDocument,
} from "@/lib/providerDocuments";

const REQUIRED: { type: ProviderDocumentType; labelKey: string; hintKey: string }[] = [
  { type: ProviderDocumentType.Ine, labelKey: "provider.onb.ine", hintKey: "provider.onb.ineHint" },
  { type: ProviderDocumentType.ProofOfAddress, labelKey: "provider.onb.proof", hintKey: "provider.onb.proofHint" },
  { type: ProviderDocumentType.Curp, labelKey: "provider.onb.curp", hintKey: "provider.onb.curpHint" },
  { type: ProviderDocumentType.ActaConstitutiva, labelKey: "provider.onb.acta", hintKey: "provider.onb.actaHint" },
];

function statusBadge(s: ProviderDocumentStatus, t: (k: string) => string): { label: string; cls: string } {
  if (s === ProviderDocumentStatus.Verified) return { label: t("provider.onb.verified"), cls: "bg-emerald-100 text-emerald-700" };
  if (s === ProviderDocumentStatus.Rejected) return { label: t("provider.onb.rejected"), cls: "bg-coral/10 text-coral" };
  return { label: t("provider.onb.pending"), cls: "bg-sand/60 text-charcoal" };
}

export default function ProveedorOnboardingPage() {
  const { t } = useTranslation("provider");
  const [docs, setDocs] = useState<ProviderDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyType, setBusyType] = useState<ProviderDocumentType | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputs = useRef<Record<number, HTMLInputElement | null>>({});

  const load = () => {
    setLoading(true);
    listProviderDocuments()
      .then(setDocs)
      .catch(() => setDocs([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  // Latest document per type.
  const byType = new Map<ProviderDocumentType, ProviderDocument>();
  for (const d of docs) if (!byType.has(d.type)) byType.set(d.type, d);

  const onPick = async (type: ProviderDocumentType, file: File | undefined) => {
    if (!file) return;
    setError(null);
    setBusyType(type);
    try {
      await uploadProviderDocument(type, file);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : t("provider.onb.uploadError"));
    } finally {
      setBusyType(null);
    }
  };

  const onDelete = async (id: string, type: ProviderDocumentType) => {
    setBusyType(type);
    try { await deleteProviderDocument(id); load(); }
    catch { /* surfaced by refresh */ }
    finally { setBusyType(null); }
  };

  return (
    <div className="min-h-screen bg-offwhite pt-14 md:pt-20 pb-12">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-3xl mx-auto pt-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-charcoal mb-2">{t("provider.onb.title")}</h1>
            <p className="text-sm text-gray-500">{t("provider.onb.desc")}</p>
          </div>

          {error && <div className="mb-4 text-sm text-coral bg-coral/10 rounded-xl px-4 py-2">{error}</div>}

          <div className="bg-white rounded-2xl border border-gray-100 p-6 md:p-8 space-y-4">
            {loading ? (
              <div className="py-8 text-center text-gray-400 text-sm">{t("provider.loading")}</div>
            ) : (
              REQUIRED.map((req) => {
                const doc = byType.get(req.type);
                const badge = doc ? statusBadge(doc.status, t) : null;
                const busy = busyType === req.type;
                return (
                  <div key={req.type} className="flex items-center justify-between gap-4 p-4 bg-gray-50 rounded-xl">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-charcoal">{t(req.labelKey)}</p>
                      <p className="text-xs text-gray-400">{t(req.hintKey)}</p>
                      {doc && (
                        <a href={doc.fileUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-ocean hover:underline truncate inline-block max-w-[200px]">
                          {doc.fileName}
                        </a>
                      )}
                      {doc?.status === ProviderDocumentStatus.Rejected && doc.reviewNote && (
                        <p className="text-xs text-coral mt-0.5">{doc.reviewNote}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {badge && <span className={`text-xs font-medium px-2 py-1 rounded-full ${badge.cls}`}>{badge.label}</span>}
                      <input
                        ref={(el) => { inputs.current[req.type] = el; }}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,application/pdf"
                        className="hidden"
                        onChange={(e) => onPick(req.type, e.target.files?.[0])}
                      />
                      <button
                        onClick={() => inputs.current[req.type]?.click()}
                        disabled={busy}
                        className="text-xs bg-ocean text-white px-3 py-1.5 rounded-full hover:bg-ocean/90 disabled:opacity-50"
                      >
                        {busy ? t("provider.onb.uploading") : doc ? t("provider.onb.replace") : t("provider.onb.uploadShort")}
                      </button>
                      {doc && (
                        <button onClick={() => onDelete(doc.id, req.type)} disabled={busy} className="text-xs text-coral hover:underline disabled:opacity-50">
                          {t("provider.onb.delete")}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className="text-center mt-6">
            <Link to="/proveedor/empresa" className="text-sm text-ocean hover:underline">{t("provider.onb.editCompany")}</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
